import {
  CollectDataStepCreateReason,
  CollectDataStepStatus,
  CollectDataStepType,
  RecollectionDeadlineType,
} from 'src/constants/collect-data/enums'
import {
  CollectDataConfig,
  CollectDataStep,
  VehicleInsuranceCollectDataStep,
} from 'src/interfaces/collectData'
import {
  collectDataStepDataMap,
  recollectionStepInstructions,
} from 'src/constants/collect-data/constants'
import { replaceTrainingWording } from 'src/utils/certificationWording'
import type { ReactNode } from 'react'

export const DEPRECATED_STITCH_PAYMENT_STEP_TYPE = 'stitch_payment'

export const isDeprecatedStitchPaymentStepType = (stepType: string): boolean => {
  return stepType === DEPRECATED_STITCH_PAYMENT_STEP_TYPE
}

export const getHasCompletedPaymentStep = (configs: CollectDataConfig[]): boolean => {
  return configs.some((config) => {
    return config.collectDataSteps.some((step) => {
      const isPaymentStep =
        step.type === CollectDataStepType.PaymentMethod ||
        isDeprecatedStitchPaymentStepType(step.type)

      return isPaymentStep && step.status === CollectDataStepStatus.Completed
    })
  })
}

export const filterDeprecatedCollectDataSteps = <T extends { type: string }>(steps: T[]): T[] => {
  return steps.filter((step) => {
    return !isDeprecatedStitchPaymentStepType(step.type)
  })
}

export const backendSortSteps = (steps: CollectDataStep[]): CollectDataStep[] => {
  return steps.sort((a, b) => a.order - b.order)
}

export const clientSortSteps = (steps: CollectDataStep[]): CollectDataStep[] => {
  return steps.sort((a, b) => {
    if (a.type === CollectDataStepType.Form) {
      return -1
    } else if (b.type === CollectDataStepType.Form) {
      return 1
    }

    if (a.type === CollectDataStepType.DriverLicense) {
      return -1
    } else if (b.type === CollectDataStepType.DriverLicense) {
      return 1
    }

    if (a.type === CollectDataStepType.VehicleInsurance) {
      return -1
    } else if (b.type === CollectDataStepType.VehicleInsurance) {
      return 1
    }

    if (a.type === CollectDataStepType.InsuranceDeclaration) {
      return -1
    } else if (b.type === CollectDataStepType.InsuranceDeclaration) {
      return 1
    }

    if (a.type === CollectDataStepType.VehicleRegistration) {
      return -1
    } else if (b.type === CollectDataStepType.VehicleRegistration) {
      return 1
    }

    if (a.type === CollectDataStepType.Vaccination) {
      return -1
    } else if (b.type === CollectDataStepType.Vaccination) {
      return 1
    }

    if (a.type === CollectDataStepType.BGC) {
      return -1
    } else if (b.type === CollectDataStepType.BGC) {
      return 1
    }

    if (a.type === CollectDataStepType.InsuranceBeneficiary) {
      return -1
    } else if (b.type === CollectDataStepType.InsuranceBeneficiary) {
      return 1
    }

    if (a.type === CollectDataStepType.SignContract) {
      return -1
    } else if (b.type === CollectDataStepType.SignContract) {
      return 1
    }

    if (a.type === CollectDataStepType.MVR) {
      return -1
    } else if (b.type === CollectDataStepType.MVR) {
      return 1
    }

    if (a.type === CollectDataStepType.EIN) {
      return -1
    } else if (b.type === CollectDataStepType.EIN) {
      return 1
    }

    return -1
  })
}

export const sortByDisabled = (steps: CollectDataConfig[]): CollectDataConfig[] => {
  return steps.sort((a, b) => {
    if (a.disabled && !b.disabled) {
      return 1
    } else if (b.disabled && !a.disabled) {
      return -1
    }

    return 0
  })
}

export const sortByCreated = (steps: CollectDataStep[]): CollectDataStep[] => {
  return steps.sort((a, b) => {
    if (a.status === CollectDataStepStatus.Created && b.status !== CollectDataStepStatus.Created) {
      return 1
    } else if (
      b.status === CollectDataStepStatus.Created &&
      a.status !== CollectDataStepStatus.Created
    ) {
      return -1
    }

    return 0
  })
}

export const getRecollectionDeadlineType = (step: CollectDataStep): RecollectionDeadlineType => {
  if (step.optional) {
    return RecollectionDeadlineType.Optional
  }

  if (step.createReason === CollectDataStepCreateReason.ManualRecollection) {
    return RecollectionDeadlineType.Repeated
  }

  if (step.expiresAt || (step.daysToExpire !== null && step.daysToExpire !== undefined)) {
    return RecollectionDeadlineType.Expiring
  }

  return RecollectionDeadlineType.NoDeadline
}

export const getStepDeadlineDays = (step: CollectDataStep): number | null => {
  return step.daysToExpire ?? null
}

export const getDeactivationDeadlineDays = (steps: CollectDataStep[]): number | null => {
  const contributingDeadlines = steps
    .filter((step) => {
      const deadlineType = getRecollectionDeadlineType(step)

      return (
        deadlineType === RecollectionDeadlineType.Expiring ||
        deadlineType === RecollectionDeadlineType.Repeated
      )
    })
    .map(getStepDeadlineDays)
    .filter((days): days is number => {
      return days !== null
    })

  if (!contributingDeadlines.length) {
    return null
  }

  return Math.min(...contributingDeadlines)
}

export const getDeactivationBannerVariant = (
  deadlineDays: number | null,
): 'amber' | 'red' | null => {
  if (deadlineDays === null) {
    return null
  }

  if (deadlineDays >= 15) {
    return 'amber'
  }

  return 'red'
}

export const getStepDisplayData = (
  step: CollectDataStep,
  isRecollection?: boolean,
): { title: string; text: string; icon: ReactNode; hideSubTitle?: boolean } => {
  const stepData = collectDataStepDataMap[step.type]({ optional: Boolean(step.optional) })

  let title = stepData.title
  let text = stepData.text
  const hideSubTitle = stepData?.hideSubTitle

  if (isRecollection && recollectionStepInstructions[step.type]) {
    text = recollectionStepInstructions[step.type] as string
  }

  if (step.type === CollectDataStepType.Form) {
    if (step.metadata?.title) {
      title = step.metadata?.title
    }

    if (step.metadata?.description) {
      text = step.metadata?.description
    }
  }

  if (
    step.type === CollectDataStepType.SignContract &&
    step.label !== CollectDataStepType.SignContract
  ) {
    title = `${title} - ${step.label}`
  }

  if (step.type === CollectDataStepType.InsuranceCoverage) {
    title = step.metadata?.insurancePolicyProduct?.insuranceProduct?.name || title
    text =
      step.metadata?.insurancePolicyProduct?.description ||
      step.metadata?.insurancePolicyProduct?.insuranceProduct?.description ||
      text
  }

  if (step.type === CollectDataStepType.VehicleInsurance) {
    const vehicleInsuranceStep = step as VehicleInsuranceCollectDataStep

    if (vehicleInsuranceStep.metadata?.disableInsuranceUpload) {
      title = 'Vehicle Information'
      text = 'Input your vehicle details.'
    }
  }

  return {
    title: replaceTrainingWording(title),
    text: replaceTrainingWording(text),
    icon: stepData.icon,
    hideSubTitle,
  }
}

export const sortRecollectionSteps = (steps: CollectDataStep[]): CollectDataStep[] => {
  return [...steps].sort((firstStep, secondStep) => {
    const firstStepDays = getStepDeadlineDays(firstStep)
    const secondStepDays = getStepDeadlineDays(secondStep)

    if (firstStepDays === null && secondStepDays === null) {
      return 0
    }

    if (firstStepDays === null) {
      return 1
    }

    if (secondStepDays === null) {
      return -1
    }

    return firstStepDays - secondStepDays
  })
}
